'use client'

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, FieldGroup } from '@nwwp/ui-shadcn'
import { RangeSelectInput } from '~/components/RangeSelectInput'
import { MoneyField, SegmentedField, SliderField } from './fields'
import type { SetField } from '../_hooks/use-calculator'
import { contributionHint, matchHint } from '../_utils/calculator'
import { months, usd0 } from '../_utils/format'
import type { CareerBreakResults, CareerBreakState, Coverage, FilingSelection } from '../types'

const FILING_OPTIONS = [
  { value: 'auto', label: 'Match my household answer' },
  { value: 'single', label: 'Single' },
  { value: 'mfj', label: 'Married filing jointly' },
  { value: 'hoh', label: 'Head of household' },
  { value: 'mfs', label: 'Married filing separately' },
]

function Step({
  number,
  title,
  summary,
  children,
}: {
  number: string
  title: string
  summary?: string
  children: React.ReactNode
}) {
  return (
    <section className="rounded-xl border border-border bg-card p-5 tablet:p-7">
      <p className="text-body-xs font-bold text-green uppercase">{number}</p>
      <h2 className="text-title-sm mt-1 text-gray-darker">{title}</h2>
      {summary && <p className="mt-1 text-subtitle text-neutral-dark">{summary}</p>}
      <FieldGroup className="mt-5">{children}</FieldGroup>
    </section>
  )
}

export function InputsPanel({
  state,
  set,
  results,
}: {
  state: CareerBreakState
  set: SetField
  results: CareerBreakResults
}) {
  const isCouple = state.household === 'couple'
  // A partner's plan is only an option if there is a partner still on one.
  const partnerPlanAvailable = isCouple && state.partnerWorks === 'yes'
  const coverageOptions: { value: Coverage; label: string }[] = [
    ...(partnerPlanAvailable ? [{ value: 'partner' as const, label: "Partner's plan" }] : []),
    { value: 'cobra', label: 'COBRA' },
    { value: 'aca', label: 'Marketplace' },
  ]

  return (
    <div className="flex flex-col gap-4">
      <Step number="01" title="Your situation">
        <SliderField
          label="Your age"
          value={state.age}
          min={22}
          max={80}
          onChange={(v) => set('age', v)}
          display={state.age}
        />

        <SegmentedField
          label="Household"
          value={state.household}
          options={[
            { value: 'single', label: 'Just me' },
            { value: 'couple', label: 'Me + a partner' },
          ]}
          onChange={(v) => set('household', v)}
        />

        <MoneyField label="Your annual pre-tax income" value={state.income} onChange={(v) => set('income', v)} />

        {isCouple && (
          <>
            <MoneyField
              label="Partner's annual pre-tax income today"
              value={state.partnerIncome}
              onChange={(v) => set('partnerIncome', v)}
            />
            <SegmentedField
              label="During your break, they"
              value={state.partnerWorks}
              options={[
                { value: 'yes', label: 'Keep working' },
                { value: 'no', label: 'Stop too' },
              ]}
              onChange={(v) => set('partnerWorks', v)}
              hint={
                state.partnerWorks === 'yes'
                  ? 'Their income keeps covering part of your household spending.'
                  : 'Nobody is earning during the break, so savings covers everything.'
              }
            />
          </>
        )}

        <SliderField
          label="How long do you want to be off?"
          value={state.breakMonths}
          min={1}
          max={60}
          onChange={(v) => set('breakMonths', v)}
          display={months(state.breakMonths)}
        />

        <SliderField
          label="Household size (for health coverage)"
          value={state.householdSize}
          min={1}
          max={7}
          onChange={(v) => set('householdSize', v)}
          display={`${state.householdSize}${state.householdSize === 7 ? '+' : ''}`}
        />
      </Step>

      <Step number="02" title="What you have">
        <MoneyField
          label="Cash + taxable investments"
          value={state.liquid}
          onChange={(v) => set('liquid', v)}
          hint="Savings, checking, brokerage — only what you'd genuinely be willing to spend down. Don't include 401(k)s or IRAs."
        />
        <MoneyField
          label="Retirement accounts"
          value={state.retirementBalance}
          onChange={(v) => set('retirementBalance', v)}
          hint="Not spent during the break — used to show the long-term impact."
        />
        <MoneyField
          label="Monthly household spending today"
          value={state.monthlySpend}
          onChange={(v) => set('monthlySpend', v)}
          hint="Rent or mortgage, food, insurance, utilities, gas, child or dependent care, subscriptions, debt payments — what you actually consume. Don't count money you move into savings or investments, but count every other monthly expense."
        />
      </Step>

      <Step number="03" title="What you'd give up" summary="The part people forget to price in.">
        <SliderField
          label="Your 401(k) contribution"
          value={state.contributionPct}
          min={0}
          max={30}
          onChange={(v) => set('contributionPct', v)}
          display={`${state.contributionPct}% of pay`}
          hint={contributionHint(state)}
        />

        <SliderField
          label="Employer match"
          value={state.matchTenths}
          min={0}
          max={100}
          onChange={(v) => set('matchTenths', v)}
          display={`${(state.matchTenths / 10).toFixed(1)}% of pay`}
          hint={matchHint(state)}
          tooltip="The U.S. average employer contribution is about 4.7% of pay (Vanguard, How America Saves 2026). The most common formula is 50% of the first 6% you contribute."
        />

        <MoneyField
          label="You pay for health insurance today"
          value={state.currentPremium}
          onChange={(v) => set('currentPremium', v)}
          hint="Your payroll deduction, per month. U.S. average: $120 single, $571 family."
        />

        <SegmentedField
          label="Health coverage during the break"
          value={state.coverage}
          options={coverageOptions}
          onChange={(v) => set('coverage', v)}
          hint={results.coverageNote}
        />

        {state.coverage === 'partner' && (
          <MoneyField
            label="Monthly cost to move onto their plan"
            value={state.partnerPlanCost}
            onChange={(v) => set('partnerPlanCost', v)}
          />
        )}
      </Step>

      <Step number="04" title="What gets cheaper" summary="A break isn't only a cost. Some spending stops.">
        <SliderField
          label="Child or dependent care you'd stop paying"
          value={state.careSavings}
          min={0}
          max={5000}
          step={50}
          onChange={(v) => set('careSavings', v)}
          display={`${usd0(state.careSavings)}/mo`}
          tooltip="The national average is about $1,099/mo per child (Child Care Aware of America, 2025 price data)."
        />
        <SliderField
          label="Commuting, lunches, dry cleaning, everything else"
          value={state.otherSavings}
          min={0}
          max={3000}
          step={25}
          onChange={(v) => set('otherSavings', v)}
          display={`${usd0(state.otherSavings)}/mo`}
        />
      </Step>

      <Accordion type="single" collapsible className="rounded-xl border border-border bg-card">
        <AccordionItem value="advanced" className="border-b-0">
          <AccordionTrigger className="p-5 text-body-sm-bold text-gray-darker hover:no-underline tablet:px-7">
            Advanced assumptions
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-6 tablet:px-7">
            <FieldGroup>
              <p className="text-body-xs font-bold text-neutral-dark uppercase">Timing and returns</p>
              <SliderField
                label="Retirement age"
                value={state.retirementAge}
                min={50}
                max={75}
                onChange={(v) => set('retirementAge', v)}
                display={state.retirementAge}
              />
              <SliderField
                label="Investment return"
                value={state.returnPct}
                min={0}
                max={12}
                step={0.5}
                onChange={(v) => set('returnPct', v)}
                display={`${state.returnPct.toFixed(1)}%`}
                tooltip="An annual effective rate: 7.0% here compounds to exactly 7.0% over twelve months."
              />
              <SliderField
                label="Career start age"
                value={state.careerStartAge}
                min={16}
                max={35}
                onChange={(v) => set('careerStartAge', v)}
                display={state.careerStartAge}
                tooltip="Only used for the Social Security line. Benefits average your highest 35 years of indexed earnings, so clearing 35 working years keeps the effect small — though a high-earning year you skip can still displace a lower one. This assumes you've worked continuously since that age; if you've already taken time out, your real total is lower."
              />

              <p className="mt-2 text-body-xs font-bold text-neutral-dark uppercase">Coming back to work</p>
              <SliderField
                label="Pay when you return"
                value={state.returnPayPct}
                min={50}
                max={120}
                onChange={(v) => set('returnPayPct', v)}
                display={`${state.returnPayPct}%`}
                tooltip="Research consistently finds a re-entry pay penalty. Bertrand, Goldin & Katz (2010) found that any break of six months or more carries a large, persistent earnings penalty — and that most of the damage comes from taking time out at all, not from how long."
              />
              <SliderField
                label="Years back to full pay"
                value={state.yearsToFullPay}
                min={0}
                max={15}
                onChange={(v) => set('yearsToFullPay', v)}
                display={
                  state.yearsToFullPay === 0
                    ? 'immediately'
                    : `${state.yearsToFullPay} ${state.yearsToFullPay === 1 ? 'year' : 'years'}`
                }
              />

              <p className="mt-2 text-body-xs font-bold text-neutral-dark uppercase">Taxes</p>
              <RangeSelectInput
                id="career-break-filing"
                name="filing"
                label="Tax filing status"
                labelClassName="text-body-sm-bold text-gray-darker"
                value={state.filingSelection}
                options={FILING_OPTIONS}
                onChange={(value) => set('filingSelection', value as FilingSelection)}
                hint="Defaults from your household answer, but they are not the same thing. Unmarried partners cannot file jointly, and a single parent may qualify for Head of Household."
              />
              <SliderField
                label="State + local tax"
                value={state.stateTaxPct}
                min={0}
                max={13}
                step={0.5}
                onChange={(v) => set('stateTaxPct', v)}
                display={`${state.stateTaxPct.toFixed(1)}%`}
              />

              <p className="mt-2 text-body-xs font-bold text-neutral-dark uppercase">Buffers</p>
              <SliderField
                label="Emergency reserve"
                value={state.reserveMonths}
                min={0}
                max={12}
                onChange={(v) => set('reserveMonths', v)}
                display={
                  state.reserveMonths === 0
                    ? 'none'
                    : `${state.reserveMonths} ${state.reserveMonths === 1 ? 'month' : 'months'}`
                }
                hint={
                  state.reserveMonths === 0
                    ? 'Nothing held back'
                    : `${usd0(state.reserveMonths * state.monthlySpend)} held back`
                }
                tooltip="Cash you keep untouched for the whole break. A break makes income loss a certainty rather than a risk, so this never doubles as the sabbatical fund. NerdWallet's guidance is three to six months of expenses."
              />
              <MoneyField
                label="Cash left over at the end"
                value={state.endingCushion}
                onChange={(v) => set('endingCushion', v)}
                tooltip="Optional. Money you want still sitting in the bank the day you go back to work, on top of the emergency reserve you never touched. Leave it at 0 and we only tell you what it takes to break even."
              />
            </FieldGroup>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}
